import React from 'react';
import { Button, Space, Tag, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import SoftTag from '../components/SoftTag';
import { brandSemantic } from '../config/brand';
import { buildSupplierSummary } from './wizardHelpers';

const PENDING_TONE = { color: brandSemantic.textSecondary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted };

/**
 * Shared Inquiry-Item table columns (inquiry-module-redesign): a line item
 * is identified by Category rather than a specific Product, opening the
 * Category Details page (header fields + Selection Wizard) instead of a
 * Product Details screen — every other column (description, target price,
 * re-order, attachment, sourcing) is unchanged from before.
 */
export const buildInquiryItemColumns = ({
  t,
  onCategoryClick,
  showSourcingStatus = false,
  suppliers = [],
  onEdit,
  onDelete,
}) => {
  const columns = [
    {
      title: t('Category'),
      dataIndex: 'categoryName',
      key: 'categoryName',
      width: 130,
      render: (value, record) => {
        const label = value || <span style={{ color: '#bfbfbf' }}>—</span>;
        return onCategoryClick ? (
          <Button
            type="link"
            style={{ padding: 0, height: 'auto', fontWeight: 600 }}
            onClick={() => onCategoryClick(record)}
          >
            {label}
          </Button>
        ) : label;
      },
    },
    {
      title: t('Product Description'),
      dataIndex: 'specifications',
      key: 'specifications',
      ellipsis: true,
      render: (value) => value || <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    { title: t('QTY'), dataIndex: 'quantity', key: 'quantity', width: 70, align: 'right' },
    {
      title: t('Target Price'),
      dataIndex: 'targetPrice',
      key: 'targetPrice',
      width: 110,
      align: 'right',
      render: (value) => (value != null ? Number(value).toLocaleString('da-DK') : <span style={{ color: '#bfbfbf' }}>—</span>),
    },
    {
      title: t('Re-order'),
      dataIndex: 'reorder',
      key: 'reorder',
      width: 90,
      render: (value) => (value ? t('Yes') : t('No')),
    },
    {
      title: t('Attachment'),
      key: 'files',
      width: 110,
      render: (_, record) => ((record.files || []).length > 0
        ? `${record.files.length} ${t('file(s)')}`
        : <span style={{ color: '#bfbfbf' }}>—</span>),
    },
  ];

  if (showSourcingStatus) {
    columns.push({
      title: t('Sourcing'),
      key: 'sourcing',
      render: (_, record) => {
        const wizardResult = record.wizardResult;
        if (!wizardResult) {
          return <SoftTag tone={PENDING_TONE}>{t('Pending')}</SoftTag>;
        }
        const { selectedProducts = [], selectedServices = [], logisticsNotes = '' } = wizardResult;
        const summary = buildSupplierSummary(selectedProducts, selectedServices, suppliers);
        return (
          <Space direction="vertical" size={4}>
            {summary.map((entry) => (
              <Space key={entry.supplierId} size={4} wrap>
                <strong>{entry.supplierName}</strong>
                {entry.roles.map((role) => (
                  <Tag key={role} style={{ fontSize: 11, margin: 0 }}>{t(role)}</Tag>
                ))}
              </Space>
            ))}
            {logisticsNotes && (
              <Space key="logistics" size={4} wrap align="start">
                <Tag style={{ fontSize: 11, margin: 0 }}>{t('Logistics')}</Tag>
                <span
                  style={{ fontSize: 12, color: brandSemantic.textSecondary, maxWidth: 240 }}
                  title={logisticsNotes}
                >
                  {logisticsNotes.length > 80 ? `${logisticsNotes.slice(0, 80)}…` : logisticsNotes}
                </span>
              </Space>
            )}
          </Space>
        );
      },
    });
  }

  columns.push({
    title: t('Actions'),
    key: 'actions',
    width: 90,
    render: (_, record) => (
      <Space size={4}>
        <Tooltip title={t('Edit')}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit?.(record)} />
        </Tooltip>
        <Tooltip title={t('Delete')}>
          <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => onDelete?.(record)} />
        </Tooltip>
      </Space>
    ),
  });

  return columns;
};
